import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Tag } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

// Layout
export const Container = styled.div`
  padding: 16px 24px;
  background: ${(p) => getThemeValue(p, '#171923', '#f5f5f5')};
  color: ${(p) => getThemeValue(p, '#fff', 'inherit')};

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
`;

// Header
export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DashboardTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -1px;
  color: ${(p) => getThemeValue(p, '#f7fafc', '#1a202c')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const DashboardSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#718096')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const StatusBadge = styled(Tag)`
  border-radius: 12px;
  font-weight: 600;
  font-size: 13px;
  padding: 4px 12px;
`;

// Metrics Grid
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Metric Card
export const MetricCardWrapper = styled(Card)<{ $isSelected: boolean; $accentColor: string }>`
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid ${(p) => getThemeValue(p, '#4a5568', '#e2e8f0')};
  background: ${(p) => p.$isSelected
    ? getThemeValue(p, '#2d3748', '#f0f7ff')
    : getThemeValue(p, '#1a202c', '#fff')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  .ant-card-body {
    padding: 20px 24px;
  }
`;

export const MetricCardAccent = styled.div<{ $color: string }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  background: ${(p) => p.$color};
  opacity: 0.08;
  border-radius: 50%;
  transform: translate(20px, -20px);
`;

export const MetricCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const MetricCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetricLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#718096')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const MetricValue = styled.span<{ $color: string }>`
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
  color: ${(p) => p.$color};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

export const MetricSubtitle = styled.span`
  font-size: 13px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#718096')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const MetricIconBox = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => `${p.$color}1a`};
  color: ${(p) => p.$color};
  font-size: 18px;
  flex-shrink: 0;
`;

export const MetricTrendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TrendIndicator = styled.span<{ $isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.$isPositive ? '#48BB78' : '#E53E3E'};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const TrendBadge = styled(Tag)<{ $isPositive: boolean }>`
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
`;

// Quick Actions
export const QuickActionsCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid ${(p) => getThemeValue(p, '#4a5568', '#e2e8f0')};
  background: ${(p) => getThemeValue(p, '#1a202c', '#fff')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .ant-card-body {
    padding: 20px 24px;
  }
`;

export const QuickActionsTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 700;
  color: ${(p) => getThemeValue(p, '#90cdf4', '#2D6CCA')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const QuickActionButton = styled(Button)<{ $accentColor: string }>`
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  height: auto !important;
  padding: 12px 16px !important;
  border-color: ${(p) => p.$accentColor} !important;
  color: ${(p) => p.$accentColor} !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: ${(p) => `${p.$accentColor}0a`} !important;
    border-color: ${(p) => p.$accentColor} !important;
    color: ${(p) => p.$accentColor} !important;
  }
`;

// Section Card (tables, activities, health)
export const SectionCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid ${(p) => getThemeValue(p, '#4a5568', '#e2e8f0')};
  background: ${(p) => getThemeValue(p, '#1a202c', '#fff')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .ant-card-body {
    padding: 20px 24px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${(p) => getThemeValue(p, '#90cdf4', '#2D6CCA')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const ViewAllButton = styled(Button)`
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
`;

// Activities
export const ActivitiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ActivityItem = styled.div<{ $statusColor: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${(p) => getThemeValue(p, '#2d3748', '#f5f5f5')};
  border-left: 4px solid ${(p) => p.$statusColor};
  transition: background 0.2s ease;

  &:hover {
    background: ${(p) => getThemeValue(p, '#4a5568', '#e8e8e8')};
  }
`;

export const ActivityLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

export const ActivityIconWrapper = styled.div<{ $color: string }>`
  color: ${(p) => p.$color};
  font-size: 16px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const ActivityTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ActivityText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => getThemeValue(p, '#e2e8f0', '#1a1a1a')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ActivitySubtext = styled.span`
  font-size: 12px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#8c8c8c')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const ActivityTime = styled.span`
  font-size: 13px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#8c8c8c')};
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  flex-shrink: 0;
  margin-left: 12px;
`;

export const EmptyText = styled.div`
  text-align: center;
  padding: 32px;
  color: ${(p) => getThemeValue(p, '#a0aec0', '#8c8c8c')};
  font-size: 14px;
`;

// System Health
export const HealthRow = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const HealthLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const HealthLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => getThemeValue(p, '#e2e8f0', '#1a1a1a')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const HealthValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #48BB78;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

// Bottom Grid
export const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

// Skeleton
export const SkeletonCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(p) => getThemeValue(p, '#4a5568', '#e2e8f0')};
  background: ${(p) => getThemeValue(p, '#1a202c', '#fff')};

  .ant-card-body {
    padding: 20px;
  }
`;

// Refresh area
export const RefreshArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LiveBadge = styled(Tag)`
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
`;

export const RefreshButton = styled(Button)`
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
`;
